import React, {
  useState, useContext, useEffect, useCallback,
} from 'react';
import {
  Grid,
  ExpansionPanel,
  ExpansionPanelSummary,
  ExpansionPanelDetails,
  Typography,
  Hidden,
  makeStyles,
} from '@material-ui/core';
import { selectUsername } from 'store/ducks/auth';
import { useSelector } from 'react-redux';
import { Search as SearchIcon, ExpandMore as ExpandMoreIcon } from '@material-ui/icons';
import { useForm } from 'react-hook-form';
import pickBy from 'lodash.pickby';
import identity from 'lodash.identity';
import UPaper from 'components/UPaper';
import USelect from 'components/USelect';
import UTextField from 'components/UTextField';
import UButton from 'components/UButton';
import { useSnackbar } from 'notistack';
import CertificadoContext from '../Context/list';
import { FiltrosContext } from '../Context/filtros';
import MarcaContext from '../Context/marcas';

const useFormStyles = makeStyles((theme) => ({
  input: {
    fontSize: 16,
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 300,
  },
  selectEmpty: {
    marginTop: theme.spacing(2),
  },
  label: {
    fontSize: theme.typography.pxToRem(14),
    transform: 'none',
  },
  root: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(1),
  },
  panelSummary: {
    margin: 0,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  panelDetails: {
    padding: theme.spacing(1),
  },
  panelHeading: {
    display: 'flex',
    alignItems: 'center',
    flexBasis: '32%',
    flexShrink: 0,
  },
  panelSubHeading: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary,
    alignSelf: 'center',
  },
  panelSearchIcon: {
    marginRight: theme.spacing(3),
  },
  form: {
    width: '100%',
  },
}));

const statusPendente = 'PENDENTE';
const statusOptions = [
  { value: 'PENDENTE', label: 'Pendente' },
  { value: 'INICIADO', label: 'Iniciado Processo de Assinatura' },
  { value: 'CONCLUIDO', label: 'Concluído' },
  { value: 'AGUARDANDO_ASSINATURA', label: 'Aguardando Assinatura' },
  { value: 'INTEGRADO_COSMOS', label: 'Certificado validado e assinado' },
];

const filtrosLabel = {
  marca: 'Marca',
  status: 'Status',
  responsavel: 'Responsável',
  matricula: 'Matrícula',
  tipoCurso: 'Tipo de Curso',
  curso: 'Curso',
  cpf: 'CPF',
};

function Form() {
  const { fetchMarcas } = useContext(MarcaContext);

  const { data } = useContext(MarcaContext);
  const marcas = data.map((i) => ({
    value: i.nome, label: i.nome.toLowerCase().replace(/\b\w/g, (s) => s.toUpperCase()),
  }));

  const classes = useFormStyles();
  const userName = useSelector(selectUsername);
  const {
    handleSubmit, register, reset, control, setValue,
  } = useForm({
    defaultValues: {
      marca: null,
      status: null,
    },
  });
  const { enqueueSnackbar } = useSnackbar();
  const { error, loading, fetch } = useContext(CertificadoContext);
  const { setFiltros, toggle } = useContext(FiltrosContext);

  const [expanded, setExpanded] = useState(false);
  const handlePanelExpand = () => {
    setExpanded(!expanded);
    toggle();
  };

  const handleMarcas = useCallback(() => {
    fetchMarcas();
  }, [fetchMarcas]);

  const handleFiltrar = useCallback((filtros) => {
    const filtroValid = pickBy(filtros, identity);
    const { status, marca } = filtroValid;

    const filtro = {
      ...filtroValid,
      status: (status && status.value) ? status.value : status,
      marca: (marca && marca.value) ? marca.value : marca,
    };

    fetch(filtro);
    setFiltros(filtro, filtrosLabel);
  }, [fetch, setFiltros]);

  useEffect(() => {
    if (error && error.message) {
      const { message } = error;
      enqueueSnackbar(message, { variant: 'error' });
    }
  }, [error, enqueueSnackbar]);

  const handleLimpar = () => reset();

  useEffect(() => {
    const pendente = statusOptions.find((s) => s.value === statusPendente);
    const filtroInicial = {
      status: pendente,
      responsavel: userName,
    };
    setValue('responsavel', userName);
    setValue('status', pendente);
    handleFiltrar(filtroInicial);
    handleMarcas();
  }, [setValue, handleFiltrar, handleMarcas, userName]);

  return (
    <ExpansionPanel component={UPaper} insetSize="1x" expanded={expanded} onChange={handlePanelExpand}>
      <ExpansionPanelSummary
        classes={{ content: classes.panelSummary }}
        expandIcon={<ExpandMoreIcon />}
      >
        <Typography className={classes.panelHeading} variant="h6" component="h2">
          <SearchIcon className={classes.panelSearchIcon} />
          Filtrar
        </Typography>
        {!expanded && (
          <Hidden className={classes.panelSubHeading} smDown implementation="css">
            <Typography>
              Clique para filtrar os resultados
            </Typography>
          </Hidden>
        )}
      </ExpansionPanelSummary>
      <ExpansionPanelDetails className={classes.panelDetails}>
        <form className={classes.form} onSubmit={handleSubmit(handleFiltrar)}>
          <Grid container direction="row">
            <Grid item md={4} xs={12}>
              <USelect
                label="Marca"
                name="marca"
                fullWidth
                options={marcas}
                control={control}
              />
            </Grid>
            <Grid item md={4} xs={12}>
              <USelect
                label="Status"
                name="status"
                fullWidth
                options={statusOptions}
                control={control}
              />
            </Grid>
            <Grid item md={4} xs={12}>
              <UTextField
                label="Responsável"
                name="responsavel"
                fullWidth
                inputRef={register}
                disabled={loading}
              />
            </Grid>
            <Grid item md={4} xs={12}>
              <UTextField
                label="Matrícula"
                name="matricula"
                fullWidth
                inputRef={register}
                disabled={loading}
              />
            </Grid>
            <Grid item md={4} xs={12}>
              <UTextField
                label="Tipo de Curso"
                name="tipoCurso"
                fullWidth
                inputRef={register}
                disabled={loading}
              />
            </Grid>
            <Grid item md={4} xs={12}>
              <UTextField
                label="Curso"
                name="curso"
                fullWidth
                inputRef={register}
                disabled={loading}
              />
            </Grid>
            <Grid item md={4} xs={12}>
              <UTextField
                label="CPF"
                name="cpf"
                fullWidth
                inputRef={register}
                disabled={loading}
              />
            </Grid>
          </Grid>
          <Grid container direction="row">
            <Grid item md={2}>
              <UButton
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
              >
                Buscar
              </UButton>
            </Grid>
            <Grid item md={2}>
              <UButton
                type="button"
                variant="outlined"
                color="secondary"
                fullWidth
                onClick={handleLimpar}
              >
                Limpar

              </UButton>
            </Grid>
          </Grid>
        </form>
      </ExpansionPanelDetails>
    </ExpansionPanel>
  );
}

export default Form;
